import { defineStore } from "pinia";
import Network from "~/utils/network";
import { BNPL_LIMIT_FLOW_POSITION } from "~/utils/const";
import { useProfileStore } from "./profile";

const urls = {
  create: `/bnpl/customer-limit/create`,
  nextStep: `/bnpl/customer-limit/next-step`,
  getMyInProgressLimit: (countryId, merchantId) =>
    `/bnpl/customer-limit/get-my-in-progress-limit/${countryId}${
      !!merchantId ? `?merchantId=${merchantId}` : ""
    }`,
  getMyActiveLimit: (countryId, merchantId) =>
    `/bnpl/customer-limit/get-my-active-limit/${countryId}${
      !!merchantId ? `?merchantId=${merchantId}` : ""
    }`,
  getMerchants: (countryId) => `/bnpl/customer-limit/merchants/${countryId}`,
  getIsCustomerLimit: (financier) =>
    `/bnpl/customer-limit/partner/is-customer-limit/${financier}`,
  getPremiumTemplate: (countryId) =>
    `/bnpl/customer-limit/limit-details-content/${countryId}`,
  getFlowId: (countryId) => `/bnpl/customer-limit/flow-id/${countryId}`,
  introKyc: (countryId) => `/bnpl/customer-limit/intro-kyc/${countryId}`,
};

export const useCustomerLimitStore = defineStore("customerLimit", {
  state: () => ({
    limit: {},
    dynamicData: {},
    merchants: [],
    premiumTemplate: "",
    isCustomerLimitEnabled: false,
    flowId: null,
    merchantId: null,
    introKycRequired: false,
  }),
  actions: {
    /**
     * Whether the intro has to be replaced by an identification. Re-run after the KYC finishes so
     * the flow can move on.
     */
    fetchIntroKyc: async function (countryId) {
      try {
        const data = await Network.http(urls.introKyc(countryId), {
          method: "GET",
        });
        this.introKycRequired = !!data.required;
        return data;
      } catch (err) {
        console.log(err);
        this.introKycRequired = false;
      }
    },
    create: async function (countryId) {
      try {
        let payload = {
          countryId,
        };
        if (this.merchantId) {
          payload.merchantId = this.merchantId;
        }
        const data = await Network.http(urls.create, {
          method: "POST",
          body: JSON.stringify(payload),
        });
        this.limit = data;
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    getMyInProgressLimit: async function (countryId) {
      try {
        const data = await Network.http(
          urls.getMyInProgressLimit(countryId, this.merchantId),
          { method: "GET" }
        );
        try {
          this.dynamicData = JSON.parse(data.dynamicData);
        } catch {}
        this.limit = data;
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    waitForLimitDecision: async function (
      countryId,
      { intervalMillis = 1000, timeoutMillis = 30000 } = {}
    ) {
      const deadline = Date.now() + timeoutMillis;
      while (Date.now() < deadline) {
        try {
          await this.getMyInProgressLimit(countryId);
        } catch {
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, intervalMillis));
      }
    },
    getMyActiveLimit: async function (countryId) {
      try {
        let data = await Network.http(
          urls.getMyActiveLimit(countryId, this.merchantId),
          { method: "GET" }
        );
        this.limit = data;
        try {
          const customerLimitExpirationDate = localStorage.getItem(
            `customer-limit-expiration-date`
          );
          if (
            !customerLimitExpirationDate ||
            new Date(customerLimitExpirationDate).getTime() <
              new Date(data.expirationDate).getTime()
          ) {
            console.debug(
              "Sending analytics events for customer limit creation."
            );
            localStorage.setItem(
              `customer-limit-expiration-date`,
              data.expirationDate
            );
            gtag("event", "sign_up");
            fbq("track", "CompleteRegistration");
            ttq.track("CustomerLimitResult");
          }
        } catch {
          console.debug(
            "Could not send analytics events for customer limit creation."
          );
        }
      } catch (err) {
        this.limit = {};
        console.log(err);
        await Promise.reject(err);
      }
    },
    getMerchants: async function (countryId) {
      try {
        let data = await Network.http(urls.getMerchants(countryId), {
          method: "GET",
        });
        if (this.merchants && this.merchants.length === 0) {
          this.merchants = data;
        }
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    fetchMerchantLogos: async function () {
      try {
        for (const index in this.merchants) {
          if (
            !(
              this.merchants &&
              this.merchants[index] &&
              this.merchants[index]["logoUrl"]
            )
          ) {
            try {
              this.merchants[index]["logoUrl"] = URL.createObjectURL(
                await Network.http(
                  `/file/public/${this.merchants[index].logo}`,
                  { responseType: "blob" }
                )
              );
            } catch {
              console.error(
                `Merchan logo was not available for merchant: ${this.merchants[index].id}`
              );
            }
          }
        }
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    // The customer-limit data step only ever collects these four baked-in fields, each backed
    // directly by the customer entity (see the Customer*Field components), so validation just
    // checks the profile rather than a scoring-questions-driven list. Blank text counts as missing,
    // same as the backend's check on the customer record before it lets the limit leave DATA.
    validateDynamicData: function () {
      const profileStore = useProfileStore();
      const isBlank = (value) =>
        !value || (typeof value === "string" && !value.trim());
      if (isBlank(profileStore.lastName)) {
        return "last_name";
      }
      if (isBlank(profileStore.firstName)) {
        return "first_name";
      }
      if (isBlank(profileStore.dateOfBirth)) {
        return "date_of_birth";
      }
      if (isBlank(profileStore.postCode)) {
        return "post_code";
      }
      return null;
    },
    getPremiumTemplate: async function (countryId) {
      try {
        const data = await Network.http(urls.getPremiumTemplate(countryId), {
          method: "GET",
        });
        this.premiumTemplate = data;
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    fetchIsCustomerLimitEnabled: async function (financier) {
      try {
        const resp = await Network.http(urls.getIsCustomerLimit(financier), {
          method: "GET",
        });
        this.isCustomerLimitEnabled = resp;
      } catch (err) {
        this.isCustomerLimitEnabled = false;
        console.log(err);
        await Promise.reject(err);
      }
    },
    fetchFlowId: async function (countryId) {
      try {
        const { flowId } = await Network.http(urls.getFlowId(countryId), {
          method: "GET",
        });
        this.flowId = flowId;
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    nextStep: async function (countryId, stepBack = false) {
      try {
        let data = await Network.http(urls.nextStep, {
          method: "POST",
          body: {
            stepBack,
            countryId,
          },
          timeout: 60000,
        });
        this.limit["flowPosition"] = data;
        if (data === BNPL_LIMIT_FLOW_POSITION.INTRO) {
          await this.fetchIntroKyc(countryId);
        }
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
  },
  getters: {
    isLimitActive: (state) => state.limit && !!state.limit.expirationDate,
  },
});
